import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCardIcon from "@mui/icons-material/AddCard";

import AppButton from "../components/buttons/AppButton";
import AppCircularProgress from "../components/common/AppCircularProgress";
import BalanceCards from "../components/studentFeeLedgers/BalanceCards";
import TransactionTable from "../components/studentFeeLedgers/TransactionTable";
import RecordTransactionModal from "../components/studentFeeLedgers/RecordTransactionModal";
import StudentFeeStatementPdfTab from "../components/studentFeeLedgers/StudentFeeStatementPdfTab";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getStudentStatement,
  resetFinanceStateAction,
} from "../redux/actions/studentFeeLedgerActions";

const StudentFeeLedgerStatementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { studentId } = useParams<{ studentId: string }>();
  const [searchParams] = useSearchParams();
  const termId = searchParams.get("termId");

  const {
    studentStatement,
    loadingStatement,
    errorLoadingStatement,
    serverStatus,
    loadingTransactionCreate,
    errorCreatingTransaction,
  } = useAppSelector((state) => state.studentFeeLedger);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [isRecordModalOpen, setIsRecordModalOpen] = useState<boolean>(false);

  const fetchData = useCallback((): void => {
    if (studentId && termId) {
      dispatch(getStudentStatement({ studentId, termId }));
    }
  }, [dispatch, studentId, termId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (serverStatus?.operation === "create" && serverStatus?.status === 201) {
      setIsRecordModalOpen(false);
      fetchData();
      dispatch(resetFinanceStateAction());
    }
  }, [serverStatus, dispatch, fetchData]);

  if (loadingStatement) return <AppCircularProgress />;
  if (errorLoadingStatement) {
    return <Alert severity="error">{errorLoadingStatement}</Alert>;
  }
  if (!studentStatement || !studentId || !termId) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      {/* Header bar matching PO design */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Stack direction="row" sx={{ alignItems: "center" }} spacing={2}>
            <IconButton
              onClick={() => navigate("/student-fee-ledgers")}
              color="inherit"
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{ fontWeight: "bold" }}
            >
              {studentStatement.studentName} ({studentStatement.admissionNumber}
              )
            </Typography>
          </Stack>

          <AppButton
            pill
            variant="contained"
            color="secondary"
            startIcon={<AddCardIcon />}
            onClick={() => setIsRecordModalOpen(true)}
          >
            Record Payment
          </AppButton>
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: { xs: 0, sm: 6 }, mb: 2 }}
        >
          Class: <b>{studentStatement.className || "N/A"}</b> • Term:{" "}
          <b>{studentStatement.termName || "N/A"}</b>
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Statement Details & Ledger" />
          <Tab label="Statement PDF" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        {activeTab === 0 && (
          <Box>
            <BalanceCards statement={studentStatement} />
            <Typography variant="h6" sx={{ mt: 4, mb: 1.5, fontWeight: 700 }}>
              Transaction History
            </Typography>
            <TransactionTable
              transactions={studentStatement.transactions || []}
            />
          </Box>
        )}

        {activeTab === 1 && (
          <StudentFeeStatementPdfTab
            statement={studentStatement}
            studentId={studentId}
            termId={termId}
          />
        )}
      </Box>

      {/* Record Transaction Modal */}
      {isRecordModalOpen && (
        <RecordTransactionModal
          open={isRecordModalOpen}
          onClose={() => setIsRecordModalOpen(false)}
          studentId={studentId}
          termId={termId}
          loading={loadingTransactionCreate}
          error={errorCreatingTransaction}
        />
      )}
    </Box>
  );
};

export default StudentFeeLedgerStatementPage;
